import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Filter, SlidersHorizontal } from 'lucide-react';
import api from '../../api/public';

const COVERS = ['#0f2044','#042d1e','#2d1d06','#1a0d2e','#0a1f2e','#2d0a0a'];
const CCOLORS = ['#60a5fa','#34d399','#fbbf24','#a78bfa','#38bdf8','#f87171'];

export default function OpacCatalogPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [q, setQ] = useState(params.get('q') || '');
  const [page, setPage] = useState(1);
  const [collectionId, setCollectionId] = useState('');
  const [availability, setAvailability] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [view, setView] = useState<'grid'|'list'>('grid');

  useEffect(() => { setQ(new URLSearchParams(location.search).get('q') || ''); }, [location.search]);

  const { data, isLoading } = useQuery({
    queryKey: ['opac-books', q, page, collectionId, availability, sortBy],
    queryFn: () => api.get(`/books?q=${q}&page=${page}&limit=24&collection_id=${collectionId}&availability=${availability}&sort=${sortBy}`),
  } as any);

  const { data: colls } = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.get('/books/collections'),
  } as any);

  const books = (data as any)?.data?.books || [];
  const total = (data as any)?.data?.total || 0;
  const totalPages = Math.ceil(total / 24);

  return (
    <div style={{paddingTop:24}}>
      <div style={{marginBottom:24}}>
        <h1 style={{fontSize:22,fontWeight:700,color:'#f1f5f9',margin:'0 0 6px'}}>კატალოგი</h1>
        <p style={{fontSize:13,color:'#475569',margin:0}}>{total.toLocaleString()} წიგნი</p>
      </div>

      <div style={{display:'flex',gap:12,marginBottom:24,flexWrap:'wrap'}}>
        <div style={{position:'relative',flex:1,minWidth:200}}>
          <Search size={15} style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#475569'}} />
          <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
            placeholder="ძიება..."
            style={{width:'100%',background:'#0d1424',border:'1px solid #1e2d45',borderRadius:8,padding:'9px 12px 9px 36px',fontSize:13,color:'#e2e8f0',outline:'none',boxSizing:'border-box'}} />
        </div>
        <select value={collectionId} onChange={e => { setCollectionId(e.target.value); setPage(1); }}
          style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:8,padding:'9px 12px',fontSize:13,color:'#94a3b8',outline:'none',cursor:'pointer'}}>
          <option value="">ყველა კოლექცია</option>
          {((colls as any)?.data?.collections||[]).map((c: any) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select value={availability} onChange={e => { setAvailability(e.target.value); setPage(1); }}
          style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:8,padding:'9px 12px',fontSize:13,color:'#94a3b8',outline:'none',cursor:'pointer'}}>
          <option value="">სტატუსი</option>
          <option value="available">ხელმისაწვდომი</option>
          <option value="unavailable">გაცემული</option>
        </select>
        <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
          style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:8,padding:'9px 12px',fontSize:13,color:'#94a3b8',outline:'none',cursor:'pointer'}}>
          <option value="newest">ახალი</option>
          <option value="oldest">ძველი</option>
          <option value="title">სათაური</option>
          <option value="author">ავტორი</option>
        </select>
        <div style={{display:'flex',gap:4}}>
          {(['grid','list'] as const).map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{padding:'8px 12px',background:view===v?'#1d4ed8':'#0d1424',border:`1px solid ${view===v?'#1d4ed8':'#1e2d45'}`,borderRadius:8,color:view===v?'white':'#64748b',cursor:'pointer',fontSize:13}}>
              {v==='grid'?'⊞':'☰'}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <div style={{textAlign:'center',padding:48,color:'#475569'}}>იტვირთება...</div>}

      {view === 'grid' ? (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:16,marginBottom:24}}>
          {books.map((b: any, i: number) => {
            const ci = i % 6;
            return (
              <Link key={b.id} to={`/opac/book/${b.id}`} style={{textDecoration:'none'}}>
                <div style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:10,overflow:'hidden',transition:'all 0.2s'}}
                  onMouseEnter={e => { (e.currentTarget as any).style.transform='translateY(-3px)'; (e.currentTarget as any).style.borderColor='#2a3f6e'; }}
                  onMouseLeave={e => { (e.currentTarget as any).style.transform='translateY(0)'; (e.currentTarget as any).style.borderColor='#1e2d45'; }}>
                  {b.cover_url ? (
                    <img src={b.cover_url} alt={b.title} style={{width:'100%',height:180,objectFit:'cover'}} onError={e => {(e.target as any).style.display='none';}} />
                  ) : (
                    <div style={{height:180,background:COVERS[ci],display:'flex',alignItems:'center',justifyContent:'center',fontSize:40,fontWeight:700,color:CCOLORS[ci]}}>
                      {(b.title||'?')[0]}
                    </div>
                  )}
                  <div style={{padding:10}}>
                    <div style={{fontSize:12,fontWeight:500,color:'#cbd5e1',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginBottom:3}}>{b.title}</div>
                    <div style={{fontSize:11,color:'#475569',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginBottom:6}}>{b.author||'—'}</div>
                    <span style={{padding:'2px 6px',borderRadius:4,fontSize:10,fontWeight:500,
                      background:b.available_copies>0?'#042d1e':'#2d0a0a',
                      color:b.available_copies>0?'#34d399':'#f87171'}}>
                      {b.available_copies>0?`${b.available_copies} ხელმ.`:'გაცემული'}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div style={{marginBottom:24}}>
          {books.map((b: any, i: number) => {
            const ci = i % 6;
            return (
              <Link key={b.id} to={`/opac/book/${b.id}`} style={{textDecoration:'none'}}>
                <div style={{display:'flex',gap:12,padding:'12px 16px',background:'#0d1424',border:'1px solid #1e2d45',borderRadius:10,marginBottom:8,transition:'all 0.15s'}}
                  onMouseEnter={e => (e.currentTarget.style.borderColor='#2a3f6e')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor='#1e2d45')}>
                  {b.cover_url ? (
                    <img src={b.cover_url} alt={b.title} style={{width:44,height:56,objectFit:'cover',borderRadius:4,flexShrink:0}} onError={e => {(e.target as any).style.display='none';}} />
                  ) : (
                    <div style={{width:44,height:56,borderRadius:4,background:COVERS[ci],display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,fontWeight:700,color:CCOLORS[ci],flexShrink:0}}>
                      {(b.title||'?')[0]}
                    </div>
                  )}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:500,color:'#cbd5e1',marginBottom:3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.title}</div>
                    <div style={{fontSize:12,color:'#475569',marginBottom:4}}>{b.author||'—'}</div>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      {b.collection_name && <span style={{fontSize:11,color:'#475569'}}>{b.collection_name}</span>}
                      {b.isbn && <span style={{fontSize:10,color:'#334155',fontFamily:'monospace'}}>{b.isbn}</span>}
                    </div>
                  </div>
                  <span style={{padding:'3px 10px',borderRadius:6,fontSize:11,fontWeight:500,flexShrink:0,alignSelf:'center',
                    background:b.available_copies>0?'#042d1e':'#2d0a0a',
                    color:b.available_copies>0?'#34d399':'#f87171'}}>
                    {b.available_copies>0?`${b.available_copies} ხელმ.`:'გაცემული'}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,paddingBottom:32}}>
        <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
          style={{padding:'8px 16px',background:'#0d1424',border:'1px solid #1e2d45',borderRadius:8,fontSize:13,color:page===1?'#334155':'#94a3b8',cursor:page===1?'not-allowed':'pointer'}}>← წინა</button>
        <span style={{fontSize:13,color:'#475569'}}>{page} / {totalPages}</span>
        <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page>=totalPages}
          style={{padding:'8px 16px',background:'#0d1424',border:'1px solid #1e2d45',borderRadius:8,fontSize:13,color:page>=totalPages?'#334155':'#94a3b8',cursor:page>=totalPages?'not-allowed':'pointer'}}>შემდეგი →</button>
      </div>
    </div>
  );
}
